import React, { useState } from "react";
import { VictoryChart, VictoryLine, VictoryAxis, VictoryLegend } from "victory";
import { useSO01 } from "../../hooks/BWOS/useSO01";
import styled from "styled-components";
import { toBWOSChartTime } from "../../utils/Converters";
import useMeasure from "react-use-measure";
import { useSO01history } from "../../hooks/BWOS/useSO01history";
import { useOP13 } from "../../hooks/BWOS/useOP13.js";

const StyledChart = styled.div`
  width: 100%;
  height: 100%;
  padding: 1rem;
`;

const StyledHeading = styled.h5`
  color: var(--color-grey-500);
  padding-top: 1em;
  padding-left: 2em;
  margin-bottom: -1rem;
`;

function DepalChart() {
  const [ref, bounds] = useMeasure(); //Add on to make the chart adaptive to samller monitors
  const [chartStartTime] = useState(new Date(2000, 0, 1, 4, 0)); // 06:00
  const [chartEndTime] = useState(new Date(2000, 0, 1, 23, 59)); // 18:00
  const chartWidth = bounds.width || 800;
  const chartHeight = bounds.width ? bounds.width / 2.2 : 360;

  const {
    data: yesterdayData,
    isLoading: loadingY,
    error: errorY,
  } = useSO01history();

  const { data: todayData, isLoading: loadingT, error: errorT } = useSO01();

  if (loadingY || loadingT) return <div>Loading...</div>;
  if (errorY || errorT) return <div>Error loading chart data</div>;
  if (!yesterdayData?.length && !todayData?.length)
    return <div>No data available</div>;

  const parseData = (rows, startTime, endTime) =>
    rows
      .map((row) => {
        const x = toBWOSChartTime(row.o_TIME_FROM, startTime, endTime);
        const y = parseInt(row.o_CNTDEPALCASES, 10);
        return x && !isNaN(y) ? { x, y } : null;
      })
      .filter(Boolean);

  function parseTodayDepalData(rows, startTime, endTime) {
    const full = [];
    const withPrediction = [];
    let predictedPoint = null;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const isCurrent = row.o_CNTLOCOCCTRAY?.toLowerCase() === "current time";
      const x = toBWOSChartTime(row.o_TIME_FROM, startTime, endTime);
      const y = parseInt(row.o_CNTDEPALCASES, 10);
      if (!x || isNaN(y)) continue;

      full.push({ x, y });

      if (isCurrent && i > 0) {
        const prev = rows[i - 1];
        const xCurrent = toBWOSChartTime(row.o_TIME_FROM, startTime, endTime);
        const yCurrent = parseInt(row.o_CNTDEPALCASES, 10);

        const currentDate = new Date(row.o_TIME_FROM);
        const minutes =
          currentDate.getMinutes() + currentDate.getSeconds() / 60;
        const percentElapsed = minutes / 60;

        const projected =
          percentElapsed > 0.05
            ? Math.round(yCurrent / percentElapsed)
            : yCurrent;

        // Add all points except the current
        withPrediction.push(...full.slice(0, -1));

        // Add predicted final point
        withPrediction.push({
          x: xCurrent,
          y: projected,
        });

        break;
      }
    }

    return { full, withPrediction };
  }

  const { full: todayBlack, withPrediction: todayOrange } = parseTodayDepalData(
    todayData ?? [],
    chartStartTime,
    chartEndTime
  );

  const yesterday = parseData(
    yesterdayData ?? [],
    chartStartTime,
    chartEndTime
  );

  return (
    <StyledChart ref={ref}>
      <StyledHeading>Depal Cases</StyledHeading>
      <VictoryChart
        width={chartWidth}
        height={chartHeight}
        domainPadding={5}
        scale={{ x: "time" }}
      >
        <VictoryAxis
          tickFormat={(t) => `${t.getHours()}:00`}
          fixLabelOverlap
          style={{
            tickLabels: { fill: "#ccc", fontSize: 10 },
            axis: { stroke: "#ccc" },
            grid: { stroke: "#444", strokeDasharray: "4" },
          }}
        />
        <VictoryAxis
          dependentAxis
          style={{
            tickLabels: { fill: "#ccc", fontSize: 10 },
            axis: { stroke: "#ccc" },
            grid: { stroke: "#444", strokeDasharray: "4" },
          }}
        />

        <VictoryLine
          data={yesterday}
          interpolation="monotoneX"
          style={{ data: { stroke: "steelblue" } }}
        />
        <VictoryLine
          data={todayBlack}
          interpolation="monotoneX"
          style={{ data: { stroke: "black" } }}
        />

        <VictoryLine
          data={todayOrange}
          interpolation="monotoneX"
          style={{ data: { stroke: "orange", strokeWidth: 1.5 } }}
        />

        <VictoryLegend
          orientation="horizontal"
          gutter={20}
          x={40}
          style={{
            labels: { fill: "#ccc", fontSize: 12 },
          }}
          itemsPerRow={3}
          data={[
            { name: "Yesterday", symbol: { fill: "steelblue" } },
            { name: "Today", symbol: { fill: "orange" } },
          ]}
        />
      </VictoryChart>
    </StyledChart>
  );
}

export default DepalChart;
